import React, { useState, useEffect } from 'react';

import { BrowserRouter, Routes, Route, Navigate, useLocation, useNavigate } from 'react-router-dom';
import { Capacitor } from '@capacitor/core';
import { AppProvider, useApp } from './context/AppContext';
import { SubscriptionProvider } from './context/SubscriptionContext';
import { PomodoroProvider, usePomodoro } from './context/PomodoroContext';
import PomodoroCompletionModal from './components/PomodoroCompletionModal';
import { initDebugLogger } from './utils/debugLogger';
import { captureReferralCodes } from './services/stripe';
import LandingPage from './components/LandingPage';
import SurferLandingPage from './components/SurferLandingPage';
import WellnessLandingPage from './components/WellnessLandingPage';
import HustleLandingPage from './components/HustleLandingPage';
import LoginScreen from './components/LoginScreen';
import DashboardPage from './pages/DashboardPage';
import HabitsPage from './pages/HabitsPage';
import TasksPage from './pages/TasksPage';
import GoalsPage from './pages/GoalsPage';
import EventsPage from './pages/EventsPage';
import SettingsPage from './pages/SettingsPage';
import UpgradePage from './pages/UpgradePage';
import AdminPage from './pages/AdminPage';
import TermsPage from './pages/TermsPage';
import PrivacyPage from './pages/PrivacyPage';
import AboutPage from './pages/AboutPage';
import PWAInfoPage from './pages/PWAInfoPage';
import OAuthCallback from './pages/OAuthCallback';
import OnboardingPage from './pages/OnboardingPage';
import ScoringExplainerPage from './pages/ScoringExplainerPage';
import PomodoroExplainerPage from './pages/PomodoroExplainerPage';
import LegendaryStreakCelebration from './components/LegendaryStreakCelebration';
import InstallPWA from './components/InstallPWA';
import { SyncIndicator } from './components/LoadingSkeleton';
import PomodoroNavWidget from './components/PomodoroNavWidget';
import { AgentProvider } from './agent/AgentContext';
import { getColors, fonts } from './theme';
import { applyDensity } from './utils/density';


// Initialize iOS status bar style using SystemBars (Capacitor 8 built-in)
const initStatusBar = async () => {
  if (Capacitor.isNativePlatform()) {
    try {
      // SystemBars is built into Capacitor 8 core - no separate plugin needed
      const { Capacitor: Cap } = await import('@capacitor/core');
      const { SystemBars } = Cap.Plugins;
      
      if (SystemBars) {
        // DARK style = light content (white icons/text) on dark background
        await SystemBars.setStyle({ style: 'DARK' });
        console.log('[iOS] SystemBars configured: light content style');
      }
    } catch (error) {
      console.log('[iOS] SystemBars not available:', error.message);
    }
  }
};
initStatusBar();

// Capture affiliate/promo codes from URL on app load
captureReferralCodes();

// html and body don't scroll (index.css); #root is the page's scroll container
const getScroller = () => document.getElementById('root') || document.scrollingElement;

// Scroll to top on route change
const ScrollToTop = () => {
  const { pathname } = useLocation();
  
  useEffect(() => {
    getScroller()?.scrollTo(0, 0);
  }, [pathname]);
  
  return null;
};

// Listen for native iOS tab bar navigation events
const NativeTabListener = () => {
  const navigate = useNavigate();
  
  useEffect(() => {
    if (!Capacitor.isNativePlatform()) return;
    
    const handleNativeTabChange = (event) => {
      const { path } = event.detail;
      if (path) {
        navigate(path);
      }
    };
    
    window.addEventListener('nativeTabChange', handleNativeTabChange);
    return () => window.removeEventListener('nativeTabChange', handleNativeTabChange);
  }, [navigate]);
  
  return null;
};

// Listen for native iOS onboarding completion and auth requests
const NativeOnboardingListener = () => {
  const { addHabits, addTasks, addEvent, addGoal, updateCalendar, updateUserData } = useApp();
  const navigate = useNavigate();
  
  useEffect(() => {
    if (!Capacitor.isNativePlatform()) return;
    
    // Handle onboarding data from native Swift UI
    const handleOnboardingComplete = async (event) => {
      const data = event.detail;
      
      if (data.skipped) {
        // Just mark onboarding as complete
        if (updateUserData) {
          await updateUserData({ hasCompletedOnboarding: true });
        }
        navigate('/');
        return;
      }
      
      try {
        // Add habits
        if (data.habits && data.habits.length > 0) {
          const habitsToAdd = data.habits.map(h => ({
            name: h.name,
            timeOfDay: h.timeOfDay || 'anytime',
            goal: h.goal || 30,
            frequency: h.frequency || 'daily'
          }));
          await addHabits(habitsToAdd);
        }
        
        // Add tasks
        if (data.tasks && data.tasks.length > 0) {
          const tasksToAdd = data.tasks.map(t => ({
            title: t.title,
            priority: t.priority || 'medium',
            category: t.category || 'general'
          }));
          await addTasks(tasksToAdd);
        }
        
        // Add events
        if (data.events && data.events.length > 0) {
          for (const event of data.events) {
            await addEvent({
              name: event.name,
              date: event.date,
              startTime: event.time || null,
              calendarId: 'default'
            });
          }
        }
        
        // Add goal
        if (data.goal && data.goal.title) {
          const milestones = (data.goal.milestones || []).map((text, index) => ({
            id: `ms-${Date.now()}-${index}`,
            title: text,
            completed: false
          }));
          
          await addGoal({
            title: data.goal.title,
            targetDate: data.goal.targetDate || new Date(Date.now() + 90 * 24 * 60 * 60 * 1000).toISOString(),
            milestones: milestones,
            linkedHabitIds: []
          });
        }
        
        // Mark onboarding complete
        if (updateUserData) {
          await updateUserData({ hasCompletedOnboarding: true });
        }
        
        navigate('/');
      } catch (error) {
        console.error('[NativeOnboarding] Error saving data:', error);
        navigate('/');
      }
    };
    
    // Handle login/auth requests from native UI
    const handleLoginRequest = async (event) => {
      const { action, email, password, displayName } = event.detail;
      
      try {
        // Import Firebase auth functions dynamically
        const { signInWithEmailAndPassword, createUserWithEmailAndPassword, 
                sendPasswordResetEmail, GoogleAuthProvider, signInWithPopup,
                updateProfile } = await import('firebase/auth');
        const { auth } = await import('./firebase');
        
        switch (action) {
          case 'signIn':
            await signInWithEmailAndPassword(auth, email, password);
            window.webkit?.messageHandlers?.nativeAuth?.postMessage({
              action: 'loginSuccess',
              userName: auth.currentUser?.displayName || '',
              userEmail: email
            });
            break;
            
          case 'signUp':
            const result = await createUserWithEmailAndPassword(auth, email, password);
            if (displayName) {
              await updateProfile(result.user, { displayName });
            }
            window.webkit?.messageHandlers?.nativeAuth?.postMessage({
              action: 'loginSuccess',
              userName: displayName || '',
              userEmail: email
            });
            break;
            
          case 'resetPassword':
            await sendPasswordResetEmail(auth, email);
            window.webkit?.messageHandlers?.nativeAuth?.postMessage({
              action: 'resetSuccess'
            });
            break;
            
          case 'googleSignIn':
            const provider = new GoogleAuthProvider();
            await signInWithPopup(auth, provider);
            window.webkit?.messageHandlers?.nativeAuth?.postMessage({
              action: 'loginSuccess',
              userName: auth.currentUser?.displayName || '',
              userEmail: auth.currentUser?.email || ''
            });
            break;
            
          default:
            break;
        }
      } catch (error) {
        console.error('[NativeAuth] Error:', error);
        window.webkit?.messageHandlers?.nativeAuth?.postMessage({
          action: 'loginError',
          error: error.message
        });
      }
    };
    
    window.addEventListener('nativeOnboardingComplete', handleOnboardingComplete);
    window.addEventListener('nativeLoginRequest', handleLoginRequest);
    
    return () => {
      window.removeEventListener('nativeOnboardingComplete', handleOnboardingComplete);
      window.removeEventListener('nativeLoginRequest', handleLoginRequest);
    };
  }, [addHabits, addTasks, addEvent, addGoal, updateCalendar, updateUserData, navigate]);
  
  return null;
};

// Google Calendar Re-authentication Modal
// Google Calendar Re-authentication Modal - Removed (handled by backend)
const GoogleCalendarReauthModal = () => null;

// Loading spinner component
const LoadingScreen = () => {
  const { isDark } = useApp();
  const colors = getColors(isDark);
  
  return (
    <div 
      className="min-h-screen flex items-center justify-center"
      style={{ backgroundColor: colors.bg }}
    >
      <div className="text-center">
        <div 
          className="w-8 h-8 border-2 rounded-full animate-spin mx-auto mb-4"
          style={{ 
            borderColor: colors.border,
            borderTopColor: colors.accent
          }}
        />
        <p style={{ color: colors.textMuted }} className="text-sm">
          Loading...
        </p>
      </div>
    </div>
  );
};

const NAV_TABS = [
  { path: '/', label: 'Today' },
  { path: '/habits', label: 'Habits' },
  { path: '/tasks', label: 'Tasks' },
  { path: '/goals', label: 'Goals' },
  { path: '/events', label: 'Calendar' },
];

// Web chrome slides away while scrolling down and returns on any scroll up, so lists get the
// full height. It stays put near the top and while a focus timer runs (its widget lives in the header).
// Publishes --header-offset on <html> so sticky content can follow the header.
const HEADER_HEIGHT = 57;
const useChromeHidden = () => {
  const { isRunning } = usePomodoro();
  const location = useLocation();
  const [hidden, setHidden] = useState(false);

  useEffect(() => {
    const scroller = getScroller();
    if (!scroller) return undefined;
    let lastY = scroller.scrollTop;
    let frame = null;
    const onScroll = () => {
      if (frame) return;
      frame = requestAnimationFrame(() => {
        frame = null;
        const y = scroller.scrollTop;
        const delta = y - lastY;
        if (y < HEADER_HEIGHT * 2) setHidden(false);
        else if (delta > 6) setHidden(true);
        else if (delta < -6) setHidden(false);
        lastY = y;
      });
    };
    scroller.addEventListener('scroll', onScroll, { passive: true });
    return () => {
      scroller.removeEventListener('scroll', onScroll);
      if (frame) cancelAnimationFrame(frame);
    };
  }, []);

  // A new page starts at the top with the header showing
  useEffect(() => setHidden(false), [location.pathname]);

  const isHidden = hidden && !isRunning;
  useEffect(() => {
    document.documentElement.style.setProperty('--header-offset', isHidden ? '0px' : `${HEADER_HEIGHT}px`);
  }, [isHidden]);
  return isHidden;
};

const chromeTransition = 'transform 220ms cubic-bezier(0.2, 0, 0, 1)';

// Web header: wordmark, text tabs, focus timer, settings. Native iOS uses its own tab bar.
const PersistentHeader = ({ isOnline, isSyncing, hidden }) => {
  const { isDark } = useApp();
  const colors = getColors(isDark);
  const navigate = useNavigate();
  const location = useLocation();

  if (Capacitor.isNativePlatform()) {
    return null;
  }

  const onSettings = location.pathname === '/settings';

  return (
    <header
      className="fixed left-0 right-0 z-50 px-4 safe-area-top"
      style={{
        top: 0,
        paddingTop: 'var(--safe-area-inset-top, 0px)',
        backgroundColor: colors.bg,
        borderBottom: `1px solid ${colors.textPrimary}`,
        transform: hidden ? 'translateY(-100%)' : 'none',
        transition: chromeTransition,
      }}
    >
      <div className="max-w-6xl 2xl:max-w-[1600px] mx-auto flex items-end justify-between gap-6" style={{ minHeight: 56 }}>
        <button
          onClick={() => navigate('/')}
          className="text-[22px] leading-none pb-3.5 flex-shrink-0 transition-opacity hover:opacity-70"
          style={{ fontFamily: fonts.serif, color: colors.textPrimary }}
          aria-label="mind. today"
        >
          mind.
        </button>

        <nav className="hidden md:flex items-end gap-7 flex-1" aria-label="Pages">
          {NAV_TABS.map(({ path, label }) => {
            const isActive = location.pathname === path;
            return (
              <button
                key={path}
                onClick={() => navigate(path)}
                aria-current={isActive ? 'page' : undefined}
                className="text-sm pt-4 pb-3 transition-colors duration-150"
                style={{
                  color: isActive ? colors.textPrimary : colors.textSecondary,
                  borderBottom: `2px solid ${isActive ? colors.textPrimary : 'transparent'}`,
                  marginBottom: -1,
                }}
              >
                {label}
              </button>
            );
          })}
        </nav>

        <div className="flex items-center gap-4 pb-2.5">
          <SyncIndicator isOnline={isOnline} isSyncing={isSyncing} />
          <PomodoroNavWidget />
          <button
            onClick={() => navigate('/settings')}
            aria-current={onSettings ? 'page' : undefined}
            className="text-sm transition-colors duration-150"
            style={{
              color: onSettings ? colors.textPrimary : colors.textSecondary,
              textDecoration: onSettings ? 'underline' : 'none',
              textUnderlineOffset: 6,
            }}
          >
            Settings
          </button>
        </div>
      </div>
    </header>
  );
};

// Phone-width web tab bar (native iOS renders its own)
const BottomTabBar = ({ hidden }) => {
  const { isDark } = useApp();
  const colors = getColors(isDark);
  const navigate = useNavigate();
  const location = useLocation();

  return (
    <nav
      className="md:hidden fixed left-0 right-0 bottom-0 z-50 grid grid-cols-5"
      style={{
        backgroundColor: colors.bg,
        // A graphite rule, not ink: the page above ends in its own edge (on
        // Today, the chat's box), and two ink lines stacked read as clutter
        borderTop: `1px solid ${colors.border}`,
        paddingBottom: 'env(safe-area-inset-bottom, 0px)',
        transform: hidden ? 'translateY(100%)' : 'none',
        transition: chromeTransition,
      }}
      aria-label="Pages"
    >
      {NAV_TABS.map(({ path, label }) => {
        const isActive = location.pathname === path;
        return (
          <button
            key={path}
            onClick={() => navigate(path)}
            aria-current={isActive ? 'page' : undefined}
            className="relative text-xs"
            style={{
              minHeight: 56,
              color: isActive ? colors.textPrimary : colors.textSecondary,
              fontWeight: isActive ? 600 : 400,
            }}
          >
            {/* The current page is underlined, like Settings in the top bar,
                instead of a bar sitting on the rule */}
            <span style={{ textDecoration: isActive ? 'underline' : 'none', textUnderlineOffset: 6, textDecorationThickness: 1.5 }}>
              {label}
            </span>
          </button>
        );
      })}
    </nav>
  );
};

// Animated page wrapper with smooth transitions
const PageWrapper = ({ children, isOverview }) => {
  const [mountState, setMountState] = useState('entering'); // 'entering', 'visible', 'exiting'
  const location = useLocation();
  
  // Check if current page is Events page (needs full width)
  const isEventsPage = location.pathname === '/events';
  
  // Check if current page is Upgrade page (needs full-bleed layout like landing page)
  const isUpgradePage = location.pathname === '/upgrade';
  
  useEffect(() => {
    setMountState('entering');
    // Scroll to top on page change
    getScroller()?.scrollTo(0, 0);
    // Small delay before transitioning to visible for smooth entry
    const enterTimer = setTimeout(() => setMountState('visible'), 20);
    return () => clearTimeout(enterTimer);
  }, [location.pathname]);
  
  const getStyles = () => {
    const baseTransition = 'opacity 200ms cubic-bezier(0.2, 0, 0, 1), transform 200ms cubic-bezier(0.2, 0, 0, 1)';
    
    if (mountState === 'entering') {
      return {
        opacity: 0,
        transform: 'translateY(4px)',
        transition: baseTransition,
      };
    }
    
    return {
      opacity: 1,
      transform: 'translateY(0)',
      transition: baseTransition,
    };
  };
  
  // Check if we're on a native platform (need extra padding for tab bar, no header)
  const isNative = Capacitor.isNativePlatform();
  
  return (
    <div style={getStyles()} className="relative min-h-screen">
      {/* Subtle layered container for depth on sub-pages */}
      {!isOverview && !isUpgradePage ? (
        <>
          <div 
            className={`min-h-screen mx-auto px-4 sm:px-6 relative z-10 ${isEventsPage ? 'max-w-full' : 'max-w-6xl 2xl:max-w-[1600px]'} ${isNative ? '' : 'page-bottom-pad'}`}
            style={{ 
              // On native: safe area + native header (~70px). On web: clears the 56px header and its rule
              paddingTop: isNative ? 'calc(env(safe-area-inset-top, 44px) + 70px)' : 'calc(var(--safe-area-inset-top, 0px) + 57px)',
              // On native: tab bar overlays content (need ~120px for proper spacing)
              // On web/PWA: .page-bottom-pad clears the phone tab bar (index.css)
              paddingBottom: isNative ? 'calc(env(safe-area-inset-bottom, 34px) + 100px)' : undefined,
            }}
          >
            <div className="min-h-[calc(100vh-96px)]">
              {children}
            </div>
          </div>
        </>
      ) : (
        <div 
          className={`relative z-10 ${isNative ? '' : 'page-bottom-pad'}`}
          style={{
            // For overview/upgrade pages on iOS: safe area + native header
            paddingTop: isNative ? 'calc(env(safe-area-inset-top, 44px) + 70px)' : '0px',
            // On native: tab bar overlays content. On web/PWA: .page-bottom-pad clears the phone tab bar
            paddingBottom: isNative ? 'calc(env(safe-area-inset-bottom, 34px) + 100px)' : undefined,
          }}
        >
          {children}
        </div>
      )}
    </div>
  );
};

// Main app content with auth check
const AppContent = () => {
  const { user, authLoading, dataLoading, isDark, hasCompletedOnboarding, isOnline, isSyncing } = useApp();
  const colors = getColors(isDark);
  const location = useLocation();
  const [showLogin, setShowLogin] = useState(false);
  const chromeHidden = useChromeHidden();
  const [legendaryStreak, setLegendaryStreak] = useState(null);

  // Theme lives on <html> so the page background and paper/chalkboard texture follow it everywhere
  useEffect(() => {
    document.documentElement.setAttribute('data-theme', isDark ? 'dark' : 'light');
    applyDensity();
    document.documentElement.style.colorScheme = isDark ? 'dark' : 'light';
    // iOS status bar text must contrast with paper (dark text) or the chalkboard (light text)
    const SystemBars = Capacitor.isNativePlatform() ? Capacitor.Plugins?.SystemBars : null;
    SystemBars?.setStyle({ style: isDark ? 'DARK' : 'LIGHT' }).catch(() => {});
  }, [isDark]);
  
  // Listen for legendary streak celebration events
  useEffect(() => {
    const handleLegendaryStreak = (event) => {
      setLegendaryStreak(event.detail.streak);
    };
    
    window.addEventListener('legendary-streak', handleLegendaryStreak);
    return () => window.removeEventListener('legendary-streak', handleLegendaryStreak);
  }, []);
  
  // Public routes that don't require authentication
  const publicRoutes = ['/terms', '/privacy', '/about', '/pwa-info', '/how-scoring-works', '/pomodoro-technique'];
  const isPublicRoute = publicRoutes.includes(location.pathname);
  const isWelcomePage = location.pathname.startsWith('/welcome/');
  const isUpgrade = location.pathname === '/upgrade';
  const isOnboarding = location.pathname === '/onboarding';
  
  // Show public pages without auth check
  if (isPublicRoute) {
    return (
      <div className="relative min-h-screen overflow-hidden" style={{ backgroundColor: colors.bg }}>
        <ScrollToTop />
        <div className="relative z-10">
          <Routes>
            <Route path="/terms" element={<TermsPage />} />
            <Route path="/privacy" element={<PrivacyPage />} />
            <Route path="/about" element={<AboutPage />} />
            <Route path="/pwa-info" element={<PWAInfoPage />} />
            <Route path="/how-scoring-works" element={<ScoringExplainerPage />} />
            <Route path="/pomodoro-technique" element={<PomodoroExplainerPage />} />
          </Routes>
        </div>
      </div>
    );
  }
  
  // Debug logging for onboarding state
  console.log('[iOS-DEBUG] 📊 AppContent state:', {
    authLoading,
    dataLoading,
    hasCompletedOnboarding,
    userId: user?.userId || 'none'
  });
  
  // Show welcome/landing pages immediately without waiting for auth (only for unauthenticated users)
  if (isWelcomePage && !user && !authLoading) {
    if (showLogin) {
      return <LoginScreen onBack={() => setShowLogin(false)} />;
    }
    return (
      <>
        <ScrollToTop />
        <Routes>
          <Route path="/welcome/surfer" element={<SurferLandingPage isDark={isDark} onGetStarted={() => setShowLogin(true)} />} />
          <Route path="/welcome/mindful" element={<SurferLandingPage isDark={isDark} onGetStarted={() => setShowLogin(true)} />} />
          <Route path="/welcome/wellness" element={<WellnessLandingPage isDark={isDark} onGetStarted={() => setShowLogin(true)} />} />
          <Route path="/welcome/hustle" element={<HustleLandingPage isDark={isDark} onGetStarted={() => setShowLogin(true)} />} />
          <Route path="*" element={<LandingPage isDark={isDark} onGetStarted={() => setShowLogin(true)} />} />
        </Routes>
      </>
    );
  }
  
  // Redirect authenticated users away from welcome pages to dashboard
  if (isWelcomePage && user) {
    return <Navigate to="/" replace />;
  }
  
  // Show loading spinner while checking auth or determining onboarding status
  if (authLoading || dataLoading || hasCompletedOnboarding === null) {
    console.log('[iOS-DEBUG] ⏳ Showing LoadingScreen because:', {
      authLoading,
      dataLoading,
      hasCompletedOnboardingIsNull: hasCompletedOnboarding === null
    });
    return <LoadingScreen />;
  }
  
  // Show landing page or login for unauthenticated users
  if (!user) {
    if (showLogin) {
      return <LoginScreen onBack={() => setShowLogin(false)} />;
    }
    return (
      <>
        <ScrollToTop />
        <Routes>
          <Route path="/how-scoring-works" element={<ScoringExplainerPage />} />
          <Route path="/pomodoro-technique" element={<PomodoroExplainerPage />} />
          <Route path="/terms" element={<TermsPage />} />
          <Route path="/privacy" element={<PrivacyPage />} />
          <Route path="/about" element={<AboutPage />} />
          <Route path="/pwa-info" element={<PWAInfoPage />} />
          <Route path="/welcome/surfer" element={<SurferLandingPage isDark={isDark} onGetStarted={() => setShowLogin(true)} />} />
          <Route path="/welcome/mindful" element={<SurferLandingPage isDark={isDark} onGetStarted={() => setShowLogin(true)} />} />
          <Route path="/welcome/wellness" element={<WellnessLandingPage isDark={isDark} onGetStarted={() => setShowLogin(true)} />} />
          <Route path="/welcome/hustle" element={<HustleLandingPage isDark={isDark} onGetStarted={() => setShowLogin(true)} />} />
          <Route path="*" element={<LandingPage isDark={isDark} onGetStarted={() => setShowLogin(true)} />} />
        </Routes>
      </>
    );
  }
  
  // Main app - Persistent header on all pages
  return (
    <div className={`relative min-h-screen overflow-hidden ${!isDark ? 'light-mode' : ''}`} style={{ backgroundColor: colors.bg }}>
      <ScrollToTop />

      {!isUpgrade && !isOnboarding && <PersistentHeader isOnline={isOnline} isSyncing={isSyncing} hidden={chromeHidden} />}
      <Routes>
        <Route path="/" element={
          !hasCompletedOnboarding ? (
            <Navigate to="/onboarding" replace />
          ) : (
            <PageWrapper isOverview={true}>
              <DashboardPage />
            </PageWrapper>
          )
        } />
        <Route path="/habits" element={
          !hasCompletedOnboarding ? <Navigate to="/onboarding" replace /> : (
            <PageWrapper>
              <HabitsPage />
            </PageWrapper>
          )
        } />
        <Route path="/tasks" element={
          !hasCompletedOnboarding ? <Navigate to="/onboarding" replace /> : (
            <PageWrapper>
              <TasksPage />
            </PageWrapper>
          )
        } />
        <Route path="/goals" element={
          !hasCompletedOnboarding ? <Navigate to="/onboarding" replace /> : (
            <PageWrapper>
              <GoalsPage />
            </PageWrapper>
          )
        } />
        <Route path="/events" element={
          !hasCompletedOnboarding ? <Navigate to="/onboarding" replace /> : (
            <PageWrapper>
              <EventsPage />
            </PageWrapper>
          )
        } />
        <Route path="/settings" element={
          !hasCompletedOnboarding ? <Navigate to="/onboarding" replace /> : (
            <PageWrapper>
              <SettingsPage />
            </PageWrapper>
          )
        } />
        <Route path="/upgrade" element={
          !hasCompletedOnboarding ? <Navigate to="/onboarding" replace /> : (
            <PageWrapper>
              <UpgradePage />
            </PageWrapper>
          )
        } />
        <Route path="/admin" element={
          <PageWrapper>
            <AdminPage />
          </PageWrapper>
        } />
        <Route path="/pwa-info" element={<PWAInfoPage />} />
        <Route path="/oauth-callback" element={<OAuthCallback />} />
        <Route path="/onboarding" element={<OnboardingPage />} />
        <Route path="/how-scoring-works" element={
          <PageWrapper>
            <ScoringExplainerPage />
          </PageWrapper>
        } />
        <Route path="/pomodoro-technique" element={
          <PageWrapper>
            <PomodoroExplainerPage />
          </PageWrapper>
        } />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
      
      {/* Native iOS Tab Navigation Listener */}
      <NativeTabListener />
      
      {/* Native iOS Onboarding/Auth Listener */}
      <NativeOnboardingListener />
      
      {/* Google Calendar Re-auth Modal */}
      <GoogleCalendarReauthModal />
      
      {/* Legendary Streak Celebration Overlay */}
      {legendaryStreak && (
        <LegendaryStreakCelebration 
          streak={legendaryStreak}
          displayName={user?.displayName}
          isDark={isDark}
          onComplete={() => setLegendaryStreak(null)} 
        />
      )}
      
      {/* PWA Install Prompt - hide during onboarding */}
      {!isOnboarding && <InstallPWA />}
      
      {/* Web Bottom Tab Bar - only for non-native (PWA/web) */}
      {!Capacitor.isNativePlatform() && !isOnboarding && !isUpgrade && <BottomTabBar hidden={chromeHidden} />}
    </div>
  );
};

const UserScopedAgentProvider = ({ children }) => {
  const { user } = useApp();
  return <AgentProvider key={user?.userId || 'signed-out'}>{children}</AgentProvider>;
};

// Root app with providers
function App() {
  // Initialize debug logger to persist logs across page refreshes
  useEffect(() => {
    initDebugLogger();
  }, []);

  return (
    <AppProvider>
      <SubscriptionProvider>
        <PomodoroProvider>
          <UserScopedAgentProvider>
            <BrowserRouter>
              <AppContent />
              <PomodoroCompletionModal />
            </BrowserRouter>
          </UserScopedAgentProvider>
        </PomodoroProvider>
      </SubscriptionProvider>
    </AppProvider>
  );
}

export default App;
